import { AbdmAttachment, readAttachments } from './abdm-fhir-attachments';
import { AbdmFhirBundleIndex } from './abdm-fhir-bundle-index';
import { ABDM_RESOURCE_GROUPS, AbdmResourceGroup } from './abdm-fhir-describers';
import {
  FhirObject, asObject, joinDetails, readObjects, readReference, readString, readStringArray
} from './abdm-fhir-readers';

export interface AbdmClinicalItem {
  readonly label: string;
  readonly value: string;
  readonly detail?: string;
}

export interface AbdmClinicalGroup {
  readonly title: string;
  readonly items: AbdmClinicalItem[];
}

export interface AbdmFhirView {
  readonly date?: string;
  readonly patient?: string;
  readonly patientDetails?: string;
  readonly practitioner?: string;
  readonly organization?: string;
  readonly groups: AbdmClinicalGroup[];
  readonly attachments: AbdmAttachment[];
  readonly resourceCount: number;
}

const NARRATIVE_GROUP_TITLE = 'Clinical details';
const DEFAULT_SECTION_TITLE = 'Clinical note';

export function presentFhirBundle(fhirJson: string): AbdmFhirView | undefined {
  try {
    const bundle = asObject(JSON.parse(fhirJson));
    return bundle ? buildView(bundle) : undefined;
  } catch {
    return undefined;
  }
}

export function formatFhirJson(fhirJson: string): string {
  try {
    return JSON.stringify(JSON.parse(fhirJson), null, 2);
  } catch {
    return fhirJson;
  }
}

function buildView(bundle: FhirObject): AbdmFhirView {
  const index = new AbdmFhirBundleIndex(bundle);
  const composition = index.find('Composition');
  const patient = findReferenced(index, composition, 'subject', 'Patient');
  return {
    date: readString(composition, 'date') || readString(bundle, 'timestamp'),
    patient: readPersonName(patient),
    patientDetails: buildPatientDetails(patient),
    practitioner: readPersonName(findAuthor(index, composition)),
    organization: readString(findReferenced(index, composition, 'custodian', 'Organization'), 'name'),
    groups: buildGroups(index, composition),
    attachments: readAttachments(index),
    resourceCount: index.resources.length
  };
}

function buildGroups(index: AbdmFhirBundleIndex, composition?: FhirObject): AbdmClinicalGroup[] {
  const groups = [buildNarratives(composition), ...ABDM_RESOURCE_GROUPS.map(group => buildResourceGroup(index, group))];
  return groups.filter((group): group is AbdmClinicalGroup => group !== undefined);
}

// The resource the Composition points at, or the first of that type when it points nowhere.
function findReferenced(
  index: AbdmFhirBundleIndex, composition: FhirObject | undefined, property: string, type: string
): FhirObject | undefined {
  return index.resolve(readReference(composition, property)) || index.find(type);
}

function findAuthor(index: AbdmFhirBundleIndex, composition?: FhirObject): FhirObject | undefined {
  const author = readObjects(composition, 'author')
    .map(reference => index.resolve(readString(reference, 'reference')))
    .find(resource => resource !== undefined);
  return author || index.find('Practitioner');
}

function buildNarratives(composition?: FhirObject): AbdmClinicalGroup | undefined {
  const items = readObjects(composition, 'section')
    .map(section => ({
      label: readString(section, 'title') || DEFAULT_SECTION_TITLE,
      value: readNarrative(section)
    }))
    .filter(item => item.value.length > 0);
  return items.length ? { title: NARRATIVE_GROUP_TITLE, items } : undefined;
}

function buildResourceGroup(index: AbdmFhirBundleIndex, group: AbdmResourceGroup): AbdmClinicalGroup | undefined {
  const items = index.ofTypes(group.types).map(resource => ({
    ...group.describe(resource, index),
    detail: index.sectionTitle(resource)
  }));
  return items.length ? { title: group.title, items } : undefined;
}

function readPersonName(resource?: FhirObject): string | undefined {
  const name = readObjects(resource, 'name')[0];
  if (!name) {
    return readString(resource, 'name');
  }
  const text = readString(name, 'text');
  if (text) {
    return text;
  }
  return [...readStringArray(name, 'given'), readString(name, 'family')].filter(Boolean).join(' ') || undefined;
}

function buildPatientDetails(patient?: FhirObject): string | undefined {
  const gender = readString(patient, 'gender');
  const genderLabel = gender ? gender[0].toUpperCase() + gender.slice(1) : undefined;
  return joinDetails([genderLabel, readString(patient, 'birthDate')]);
}

function readNarrative(section: FhirObject): string {
  const markup = readString(asObject(section['text']), 'div') || '';
  const document = new DOMParser().parseFromString(markup, 'text/html');
  return document.body.textContent?.replace(/\s+/g, ' ').trim() || '';
}
